/**
 * DASHBOARD-HISTORY.WIDGET
 * Compare surveys in the time
 */

import {
  IconDefinition,
  faArrowDown,
  faArrowUp,
  faBan,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faCircleNotch,
  faMinus,
  faPlus,
  faTh,
  faTimes
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@/components/button"
import Card from "@/components/card"
import Chip from "@/components/chip"
import Dropdown from "@/components/dropdown"
import Link from "@/components/link"
import ListButton from "@/components/list-button"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import PageLoader from "@/components/page-loader"
import PagePlaceholder from "@/components/page-placeholder"
import SearchBar from "@/components/search-bar"
import Scroller from "@/components/scroller"
import Space from "@/components/space"
import { store } from "@/index"
import { groupBy, sortBy } from "lodash"
import LoadingModal from "@/modals/loading.modal"
import SelectPopulationsModal from "@/modals/select-populations.modal"
import { useEffect, useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { fetchHistory } from "@/redux/_archive.actions"
import { sessionEditAccountOptions } from "@/redux/_session.actions"
import { accountUpdateOptions } from "@/redux/account.actions"
import { Session } from "@/redux/_session.types"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVED, STATUS_SAVING } from "@/redux/_status.types"
import { Axis } from "@/redux/axis.types"
import { DashboardFilters, FilterLite, FilterState } from "@/redux/filter.types"
import { Population } from "@/redux/population.types"
import { AttributeState } from "@/redux/attribute.types"
import { surveyEditOne, surveyStatus, surveyUpdateProject } from "@/redux/survey.actions"
import { Survey, SurveyState } from "@/redux/survey.types"
import { Topic, TopicState } from "@/redux/topic.types"
import { topicFetchBySurveys } from "@/redux/topic.actions"
import Tooltip from "@/components/tooltip"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  attribute: AttributeState
  survey: SurveyState
  topic: TopicState
}

type DisplayMode = "chart" | "evolution"

interface DisplayModeIcon {
  id: DisplayMode
  value: IconDefinition
}

interface OwnProps {
  isPreview?: boolean
  currentTopics?: Topic[]
  isReport?: boolean
}

type Props = StateProps & OwnProps

const MODAL_LOADING = "MODAL_LOADING"
const MODAL_SURVEY_ADD = "MODAL_SURVEY_ADD"
const MODAL_POPULATIONS = "MODAL_POPULATIONS"

function DashboardHistoryWidget(props: Props) {
  const { t } = props

  //Display the button to edit the columns (only for the admins and if the survey is related to a project)
  const isEditButtonVisible = props._session.userRole === "ADMIN" && props.survey.active.ProjectId

  //Labels of the same question in older surveys (shared by the whole account)
  const historyAliases = props._session.accountOptions.historyAliases

  //List of modes
  const displayModeIcons: DisplayModeIcon[] = [
    { id: "chart", value: faTh },
    { id: "evolution", value: faChartBar }
  ]

  //List of results types
  const resultsTypeList = [
    { name: t("user_company_alt"), id: "company" },
    { name: t("axes"), id: "axes" },
    { name: t("topics"), id: "topics" },
    { name: t("questions"), id: "questions" }
  ]

  //Status loading
  const [isLoading, setIsLoading] = useState(true)

  //Results are acecssibles (many surveys configured)
  const [isVisible, setIsVisible] = useState(false)

  //Modal to be displayed
  const [currentModal, setCurrentModal] = useState<null | string>(null)

  const [historyScope, setHistoryScope] = useState<{
    surveyId: string
    populations: Population[]
    filters: DashboardFilters
  } | null>(null)
  const activeHistoryScope = historyScope?.surveyId === props.survey.active.id ? historyScope : null

  //List of column (surveys to be compared)
  const [columns, setColumns] = useState<Survey[]>([])

  //Current display mode
  const [displayMode, setDisplayMode] = useState<DisplayMode>("chart")

  //List of rows for the results
  const [rows, setRows] = useState<any[]>([])

  //Row selected (for updating)
  const [currentRow, setCurrentRow] = useState<any | null>(null)

  //Questionnaire of the other surveys of the project (to associate the current row)
  const [associateSurveys, setAssociateSurveys] = useState<{ survey: Survey; topics: Topic[] }[]>([])

  //Survey open in the associate modal
  const [associateSurveyId, setAssociateSurveyId] = useState<string | null>(null)

  //Search in the questions of the associate modal
  const [associateSearch, setAssociateSearch] = useState("")

  //Type of results returned (questions, topics, axis or company)
  const [resultsType, setResultsType] = useState("questions")

  //Display secondary questions
  const [showSecondaryQuestions, setShowSecondaryQuestions] = useState(false)

  //Load data
  useEffect(() => {
    loadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.filter.dashboard,
    resultsType,
    showSecondaryQuestions,
    displayMode,
    activeHistoryScope,
    props.survey.active.id,
    historyAliases
  ])

  function selectHistoryPopulations(populations: Population[]) {
    const surveyId = props.survey.active.id
    const filters = new DashboardFilters()

    if (populations.length) {
      filters.customFilters = Object.entries(groupBy(populations, "filterName")).map(([filterName, selected]) =>
        new FilterLite({
          id: filterName,
          attributesIds: props.attribute.list
            .filter((attribute) =>
              selected.some(
                (population) => population.filterName === attribute.filterName && population.name === attribute.name
              )
            )
            .map((attribute) => String(attribute.aid))
        })
      )

      // A population that no longer exists must not turn into an unfiltered request.
      if (filters.customFilters.some((filter) => !filter.attributesIds.length)) {
        filters.customFilters = [new FilterLite({ attributesIds: ["0"] })]
      }
    }

    setHistoryScope({ surveyId, populations, filters })
  }

  async function editSurvey(surveyId: string, add: boolean) {
    const projectId = add ? props.survey.active.ProjectId : null
    store.dispatch(surveyEditOne(surveyId, "ProjectId", projectId))
    store.dispatch(surveyStatus(STATUS_SAVING))
    await store.dispatch(surveyUpdateProject(surveyId, projectId))
    store.dispatch(surveyStatus(STATUS_SAVED))
  }

  async function loadHistory() {
    if (props.survey.active.ProjectId) {
      setIsLoading(true)
      store.dispatch(surveyStatus(STATUS_LOADED))

      //Get surveys associated for the project
      const surveys = sortBy(
        props.survey.list.filter((x) => x.ProjectId === props.survey.active.ProjectId),
        "dateStart"
      )

      //Load data if there is other surveys
      if (surveys.length > 1) {
        setIsVisible(true)
        const historyFilters =
          props.isReport || props.isPreview ? [] : activeHistoryScope?.filters.customFilters ?? []
        const response: any = await store.dispatch(
          fetchHistory(
            new DashboardFilters({
              ...props.filter.dashboard,
              // Separate groups intersect the history scope with dashboard selections.
              customFilters: [...props.filter.dashboard.customFilters, ...historyFilters]
            }),
            props._session.language,
            props.survey.active.ProjectId,
            resultsType,
            props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction",
            props.isReport ?? false
          )
        )

        if (!response.error) {
          formatData(response.rows, surveys)
          setColumns(displayMode === "chart" ? surveys.reverse() : surveys)
        }
      } else {
        setIsVisible(false)
      }

      setIsLoading(false)
    } else {
      setIsLoading(false)
    }
  }

  async function closeAddSurveyModal() {
    setCurrentModal(null)
    loadHistory()
  }

  function getColumnWidth(index: number) {
    return displayMode === "evolution" || index > 0 ? 64 : 130
  }

  function getRows() {
    if (props.currentTopics) {
      return rows.filter((row) => props.currentTopics?.some((topic) => topic.aid === row.Topic?.aid))
    } else {
      return rows
    }
  }

  function updateRows(
    rows,
    response,
    surveys: Survey[],
    axis: Axis | null,
    topic: Topic | null,
    label: string,
    isPrimary: boolean
  ) {
    const row = response.find((x) => x.name === label)
    const dashboardHistoryScale = props._session.dashboardDisplaySettings.dashboardHistoryScale

    if (row) {
      const values: any[] = []
      for (let i = 0; i < surveys.length; i++) {
        const survey = surveys[i]
        const note = row[survey.id] ? Math.round(row[survey.id]) / 10 : null
        const count = row[survey.id + "_count"]

        let evolution: number | null = null
        let evolutionColor: string | null = null
        let noteHeight: number | null = null
        let evolutionHeight: number | null = null

        if (note && values.length > 0) {
          const previousValue = values[values.length - 1]
          if (previousValue.note) {
            evolution = (note * 10 - previousValue.note * 10) / 10
            evolutionHeight = Math.abs(evolution * 10)

            if (evolution >= dashboardHistoryScale[3]) {
              evolutionColor = "#51e898"
            } else if (evolution >= dashboardHistoryScale[2]) {
              evolutionColor = "#99efc2"
            } else if (evolution <= dashboardHistoryScale[0]) {
              evolutionColor = "#f05146"
            } else if (evolution <= dashboardHistoryScale[1]) {
              evolutionColor = "#f1a8a3"
            }
          }
        }

        //Max 9, min 4 for the height bar
        let noteLabel = ""
        let evolutionLabel = ""
        if (note) {
          noteHeight = (note - 4) * 10
          if (noteHeight < 0) noteHeight = 0

          //Display score as satisfaction
          if (props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction") {
            noteLabel = (note * 10).toFixed(0)
            evolutionLabel = evolution ? (evolution * 10).toFixed(0) : ""
          } else {
            noteLabel = note.toFixed(1)
            evolutionLabel = evolution ? evolution.toFixed(1) : ""
          }
        }

        values.push({
          count,
          note: count < props._session.participationMin ? null : note,
          noteLabel: noteLabel,
          noteHeight: noteHeight,
          evolution,
          evolutionLabel: evolutionLabel,
          evolutionHeight,
          evolutionColor
        })
      }

      if (label === "company") {
        label = props._session.accountName
      }

      return rows.concat([
        {
          labels: [label],
          isPrimary,
          label,
          Axis: axis,
          Topic: topic,
          values: displayMode === "chart" ? values.reverse() : values
        }
      ])
    } else {
      return rows
    }
  }

  function formatData(response, surveys: Survey[]) {
    let dataRows: any = []

    if (resultsType === "company") {
      dataRows = updateRows(dataRows, response, surveys, null, null, "company", true)
    } else {
      props.topic.list.forEach((topic) => {
        if (resultsType === "axes") {
          if (topic.Axis) {
            if (!dataRows.find((x) => x.label === topic.Axis?.label)) {
              dataRows = updateRows(dataRows, response, surveys, null, null, topic.Axis?.label, true)
            }
          }
        }

        if (resultsType === "topics") {
          dataRows = updateRows(dataRows, response, surveys, topic.Axis, null, topic.label, true)
        }

        if (resultsType === "questions") {
          topic.Questions.filter((x) => x.type === "scale" && (showSecondaryQuestions || x.primaryId === null)).forEach(
            (question) => {
              dataRows = updateRows(
                dataRows,
                response,
                surveys,
                topic.Axis,
                topic,
                question.label,
                question.primaryId === null
              )
            }
          )
        }
      })
    }
    setRows(dataRows)
  }

  function participationTooltip(count: number, row) {
    return (
      <Tooltip text={t("dashboard_history_count", { count, s: count > 1 ? "s" : "" })} position="top">
        {count === 0 ? (
          <FontAwesomeIcon
            className={"grey-t" + (isAssociable ? " _hover" : "")}
            onClick={isAssociable ? () => openAssociateModal(row) : undefined}
            icon={faTimes}
          />
        ) : (
          <FontAwesomeIcon className="grey-t" icon={faBan} />
        )}
      </Tooltip>
    )
  }

  //Only questions can be associated
  const isAssociable = isEditButtonVisible && resultsType === "questions"

  async function openAssociateModal(row) {
    setCurrentRow(Object.assign({}, row, { labels: historyAliases[row.label] ?? [] }))
    setAssociateSurveyId(null)

    //Questionnaire of each other survey of the project, most recent first
    const surveys = sortBy(
      props.survey.list.filter((x) => x.ProjectId === props.survey.active.ProjectId && x.id !== props.survey.active.id),
      "dateStart"
    ).reverse()
    const responses: any[] = await Promise.all(surveys.map((survey) => store.dispatch(topicFetchBySurveys(survey.id))))
    setAssociateSurveys(
      surveys.map((survey, i) => ({
        survey,
        topics: responses[i].error ? [] : responses[i].map((topic) => new Topic(topic))
      }))
    )
  }

  //Open one survey at a time, with an empty search
  function toggleAssociateSurvey(surveyId: string) {
    setAssociateSurveyId(associateSurveyId === surveyId ? null : surveyId)
    setAssociateSearch("")
  }

  //Questions of a topic that can be associated with the current row
  function getAssociateQuestions(topic: Topic) {
    return topic.Questions.filter(
      (x) =>
        x.type === "scale" &&
        x.label !== currentRow.label &&
        x.label.toLowerCase().includes(associateSearch.toLowerCase())
    )
  }

  function associate(label, remove: boolean) {
    const labels = remove ? currentRow.labels.filter((x) => x !== label) : currentRow.labels.concat([label])
    setCurrentRow(Object.assign({}, currentRow, { labels }))
  }

  //Save in the account options : history is reloaded when the aliases change
  async function confirmAssociate() {
    const newHistoryAliases = Object.assign({}, historyAliases, { [currentRow.label]: currentRow.labels })
    if (!currentRow.labels.length) delete newHistoryAliases[currentRow.label]

    store.dispatch(surveyStatus(STATUS_SAVING))
    await store.dispatch(
      accountUpdateOptions(Object.assign({}, props._session.accountOptions, { historyAliases: newHistoryAliases }))
    )
    store.dispatch(sessionEditAccountOptions("historyAliases", newHistoryAliases))
    store.dispatch(surveyStatus(STATUS_SAVED))
    setCurrentRow(null)
  }

  //Axis and topic above a question
  function renderContext(axis: Axis | null, topic: Topic | null) {
    return (
      <>
        {axis && (
          <div style={{ color: axis.color, fontSize: 12 }}>
            <b>{t("axis")}</b> : {axis.label}
          </div>
        )}

        {topic && (
          <div style={{ fontSize: 12, marginBottom: 6 }} className="grey-t">
            <b>{t("topic")}</b> : {topic.label}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {currentModal === MODAL_LOADING && <LoadingModal />}

      {currentModal === MODAL_POPULATIONS && (
        <SelectPopulationsModal
          isSurveyActiveSelected
          isAccountSelectable
          selectedPopulations={activeHistoryScope?.populations ?? []}
          onNext={selectHistoryPopulations}
          onClose={() => setCurrentModal(MODAL_SURVEY_ADD)}
        />
      )}

      {currentModal === MODAL_SURVEY_ADD && (
        <Modal disableClickOutside isLarge status={props.survey.status}>
          <div className="flex" style={{ minHeight: 52, marginBottom: 20, alignItems: "center" }}>
            <h1 style={{ margin: 0 }}>{t("dashboard_history_empty_cta")}</h1>
            <Space />
            <Chip
              className="ml-2"
              color={activeHistoryScope?.populations.length ? props._session.accountColors.active : undefined}
              onClick={() => setCurrentModal(MODAL_POPULATIONS)}>
              {activeHistoryScope?.populations.length
                ? activeHistoryScope.populations.map((population) => population.name).join(" / ")
                : props._session.accountName}
            </Chip>
          </div>
          <Scroller isModalScroller maxHeight={300}>
            {props.survey.list
              .filter((x) => x.id !== props.survey.active.id)
              .map((survey) => (
                <ListItem key={survey.id}>
                  <div>
                    <div>{survey.name}</div>
                    <div className="grey-t">{survey.dateStartLabel}</div>
                  </div>
                  <Space />

                  {props.survey.active.ProjectId === survey.ProjectId ? (
                    <ListButton
                      icon={faMinus}
                      isPrimary
                      isFat
                      text={t("dashboard_history_survey_remove")}
                      onClick={() => editSurvey(survey.id, false)}
                    />
                  ) : (
                    <ListButton
                      icon={faPlus}
                      text={t("dashboard_history_survey_add")}
                      onClick={() => editSurvey(survey.id, true)}
                    />
                  )}
                </ListItem>
              ))}
          </Scroller>

          <div className="height-20" />

          <div className="flex">
            <Space />
            {props.survey.status === STATUS_SAVING ? (
              <Button className="light">
                <FontAwesomeIcon spin icon={faCircleNotch} />
              </Button>
            ) : (
              <Button isWithBorder className="secondary" onClick={() => closeAddSurveyModal()}>
                {t("utils_confirm")}
              </Button>
            )}
          </div>
        </Modal>
      )}

      {currentRow && (
        <Modal
          title="Associer avec une ancienne enquête"
          isCloseButtonVisible
          onClose={() => setCurrentRow(null)}
          onNext={() => confirmAssociate()}>
          <div className="grey-t">{currentRow.label}</div>

          <div className="height-20" />

          {associateSurveys.map(({ survey, topics }) => (
            <Card key={survey.id} isWithoutPadding>
              <div
                className="flex _hover"
                style={{ alignItems: "center", padding: "16px 20px" }}
                onClick={() => toggleAssociateSurvey(survey.id)}>
                <FontAwesomeIcon
                  className="grey-t"
                  icon={associateSurveyId === survey.id ? faChevronDown : faChevronRight}
                />
                <div className="width-20" />
                <div>
                  <div style={{ fontSize: 18 }}>{survey.name}</div>
                  <div className="grey-t">{survey.dateStartLabel}</div>
                </div>
              </div>

              {associateSurveyId === survey.id && (
                <div style={{ padding: "0 20px 16px" }}>
                  <SearchBar onSearch={(value) => setAssociateSearch(value)} />
                </div>
              )}

              {associateSurveyId === survey.id &&
                topics.map((topic) =>
                  getAssociateQuestions(topic).map((question, i) => (
                    <ListItem key={topic.id + question.id}>
                      <div>
                        {i === 0 && renderContext(topic.Axis, topic)}
                        {question.label}
                      </div>
                      <Space />
                      {currentRow.labels.includes(question.label) ? (
                        <ListButton
                          icon={faMinus}
                          isPrimary
                          isFat
                          tooltipPosition="left"
                          text={t("Dissocier")}
                          onClick={() => associate(question.label, true)}
                        />
                      ) : (
                        <ListButton
                          tooltipPosition="left"
                          icon={faPlus}
                          text={t("Associer")}
                          onClick={() => associate(question.label, false)}
                        />
                      )}
                    </ListItem>
                  ))
                )}
            </Card>
          ))}
        </Modal>
      )}

      <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
        {isVisible ? (
          <div className="card-exportable" style={{ backgroundColor: "white" }} id={t("dashboard_history")}>
            {!props.isPreview && (
              <div className="flex lightwhite">
                <Space />
                {resultsType === "questions" && (
                  <Link onClick={() => setShowSecondaryQuestions(!showSecondaryQuestions)}>
                    {t("dashboard_history_secondary_" + (showSecondaryQuestions ? "hide" : "show"))}
                  </Link>
                )}
                {isEditButtonVisible && (
                  <Link onClick={() => setCurrentModal(MODAL_SURVEY_ADD)}>{t("dashboard_history_empty_cta")}</Link>
                )}
              </div>
            )}
            <div className={"heatmap-header " + (props.isPreview ? "" : "lightwhite")} style={{ display: "flex" }}>
              {props.isPreview ? (
                <div style={{ width: 768 }}></div>
              ) : (
                <div className="flex">
                  <div
                    className="flex flex-dcol"
                    style={{
                      width: 436,
                      paddingLeft: 32,
                      paddingBottom: 16
                    }}>
                    <Space />

                    <div className="flex">
                      {displayModeIcons.map((icon, i) => (
                        <div key={i} style={{ paddingTop: 18 }}>
                          <ListButton
                            icon={icon.value}
                            isPrimary={icon.id === displayMode}
                            isColorActive={icon.id === displayMode}
                            onClick={() => setDisplayMode(icon.id)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="height-20" />

                    <Dropdown
                      displayField="name"
                      onSelect={(item) => setResultsType(item.id)}
                      value={resultsTypeList.find((x) => x.id === resultsType)?.name}
                      list={resultsTypeList}
                    />
                  </div>
                </div>
              )}

              {columns.map((column, i) => (
                <div key={column.id} className="heatmap-column heatmap-cell" style={{ width: getColumnWidth(i) }}>
                  <div className="heatmap-column-text flex">
                    {new Date(column.dateStart).toLocaleDateString(props._session.language, {
                      year: "numeric",
                      month: "long"
                    })}
                  </div>
                </div>
              ))}
            </div>

            {getRows().map((row, i) => (
              <ListItem key={i}>
                {showSecondaryQuestions && resultsType === "questions" && row.isPrimary && (
                  <div className="abs" style={{ borderTop: "4px solid #F0F0F0", width: "100%", top: 0, left: 0 }}></div>
                )}

                <div style={{ width: props.isPreview ? 724 : 426 }}>
                  {row.isPrimary && renderContext(row.Axis, row.Topic)}

                  <div style={{ marginLeft: !row.isPrimary ? 16 : 0 }} className={!row.isPrimary ? "grey-t" : ""}>
                    {row.label}
                  </div>
                </div>

                <div className="width-20" />

                {row.values.map((value, j) =>
                  displayMode === "evolution" ? (
                    <div
                      key={j}
                      className="flex flex-dcol medgrey-b"
                      style={{
                        width: getColumnWidth(j),
                        marginBottom: -18,
                        marginTop: -18,
                        borderBottom: "1px solid"
                      }}>
                      <Space />

                      <div className="height-20" />

                      {value.count < props._session.participationMin && (
                        <div style={{ padding: 8 }}>{participationTooltip(value.count, row)}</div>
                      )}

                      {value.note && (
                        <div
                          className="rel"
                          style={{
                            backgroundColor: "#e0e0e0",
                            height: value.noteHeight,
                            width: 30
                          }}>
                          <div
                            className="grey-t abs"
                            style={{
                              textAlign: "center",
                              bottom: -28,
                              zIndex: 1,
                              width: "100%"
                            }}>
                            {value.noteLabel}
                          </div>

                          {value.evolution !== 0 && (
                            <div className="rel">
                              <div
                                className="abs"
                                style={{
                                  width: "100%",
                                  height: value.evolutionHeight,
                                  backgroundColor: value.evolution > 0 ? "#51e898" : "#f1a8a3",
                                  top: value.evolution < 0 ? -value.evolutionHeight : undefined
                                }}></div>

                              {value.evolutionColor && (
                                <div
                                  className="abs"
                                  style={{
                                    color: value.evolutionColor,
                                    left: 32,
                                    fontSize: 12,
                                    width: 60,
                                    fontWeight: "bold"
                                  }}>
                                  {value.evolution > 0 && <span>+</span>}

                                  {value.evolutionLabel}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {value.note && (
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            backgroundColor: "#e0e0e0"
                          }}></div>
                      )}
                    </div>
                  ) : (
                    <div key={j} className="flex" style={{ width: getColumnWidth(j) }}>
                      {value.count < props._session.participationMin ? (
                        participationTooltip(value.count, row)
                      ) : (
                        <div className="flex">
                          <span className="grey-t" style={{ marginRight: 8, fontWeight: j === 0 ? "bold" : "initial" }}>
                            {value.noteLabel}
                          </span>

                          {value.evolutionColor && j === 0 && (
                            <Chip icon={value.evolution > 0 ? faArrowUp : faArrowDown} color={value.evolutionColor}>
                              {value.evolution > 0 && <span>+</span>}
                              {value.evolutionLabel}
                            </Chip>
                          )}
                        </div>
                      )}
                    </div>
                  )
                )}
              </ListItem>
            ))}
          </div>
        ) : (
          <PagePlaceholder
            title={t("dashboard_history_empty")}
            image="sherlock"
            onClick={isEditButtonVisible ? () => setCurrentModal(MODAL_SURVEY_ADD) : undefined}
            button={t("dashboard_history_empty_cta")}
          />
        )}
      </PageLoader>
    </>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  attribute: state.attribute,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(DashboardHistoryWidget))
